import React, { useEffect, useState } from "react";
import { useChatMode } from "~/providers/ChatModeProvider";
import { Message } from "~/types";
import { useGPT } from "~/hooks/useGPT";
import { generateConversationId } from "~/lib/storage";
import { useUserId } from "~/providers/UserProvider";
import { ChatHeader } from "./ChatHeader";
import { ChatScrollView } from "./ChatScrollView";
import { Panels } from "./Panel";

interface ChatContentProps {
  demo: boolean;
}

export const ChatContent = ({demo}: ChatContentProps) => {
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);
  const [isUserAttributesOpen, setIsUserAttributesOpen] = useState(false);
  const { adType } = useChatMode();
  const [message, setMessage] = useState<string>("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId] = useState<string>(generateConversationId);
  const userId = useUserId();
  const userMessages = messages.filter((message) => message.role === "user");

  const [streamingMessage, setStreamingMessage] = useState<string>("");
  const [adText, setAdText] = useState<string>();

  useEffect(() => {
    if (demo) {
      setMessages([
        {id: 'populated-msg-1', role: 'user', content: 'What is the best website builder for my small business?'},
        {id: 'populated-msg-2', role: 'assistant', content: 'The best website builder for your small business depends on your specific needs. Here are a few popular options:\n\n**Wix**: User-friendly with a drag-and-drop interface, great for visually appealing sites.\n\n**Squarespace**: Known for stunning design templates, ideal for creatives and portfolios.\n\n**Shopify**: Excellent for eCommerce, with robust features for selling products online.\n\n**WordPress.com**: Offers flexibility and control, great for blogs or more complex sites (WordPress.org is more customizable for advanced users).\n\n**Weebly**: Simple to use and good for basic eCommerce and small business sites.\n\nConsider your budget, design preferences, and whether you need eCommerce capabilities when making your choice.'}
      ])
    }
  }, [demo])

  const { mutate, isPending: isGeneratingResponse } = useGPT({
    adType: adType,
    messages,
    setStreamingMessage,
    userId,
    conversationId,
  });
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    ask(message);
  }

  function ask(message: string) {
    setIsGenerating(true);
    prepareForStream();

    // Add the user message to the messages array
    setMessages((prev) => [
      ...prev,
      { id: Math.random().toString(), role: "user", content: message },
    ]);

    const onSuccess = async () => {
      try {
            const promises = [
              window.koah?.process(message, streamingMessage, adType.toLowerCase(), demo ? {
                // hard code to ad ID & password for staging
                forcedAdId: 'report_business_toolkit', // TODO update this
                forcedAdPassword: 'af3a619b-a60e-47a2-b64e-ba8f4686a3a1'
              } : undefined),
            ];

            const results = await Promise.all(promises);
            const koahResponse = results[0];
            console.log("KOAH RESPONSE: ", koahResponse);
            if (koahResponse && koahResponse.ad_text) {
              setAdText(koahResponse.ad_text);
            }

            setIsGenerating(false);
          } catch (error) {
            console.error("Error processing message: ", error);
            setIsGenerating(false);
          }
    }

    const onSettled = (response: string, error) => {
      console.log("RESPONSE: ", response);

      if (response) {
        setStreamingMessage("");
        // Add the assistant message to the messages array
        setMessages((prev) => [
          ...prev,
          {
            id: Math.random().toString(),
            role: "assistant",
            content: response,
          },
        ]);
      }
    }

    mutate(
      { message, history: messages, ad_type: adType },
      {
        onSuccess,
        onSettled,
      },
    );
  }

  function prepareForStream() {
    setMessage("");
  }

  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setIsDebugPanelOpen(false);
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  return (
    <>
      {!demo && <ChatHeader
        isUserAttributesOpen={isUserAttributesOpen}
        isDebugPanelOpen={isDebugPanelOpen}
        setIsUserAttributesOpen={setIsUserAttributesOpen}
        setIsDebugPanelOpen={setIsDebugPanelOpen}
      />}

      <div className="flex w-full h-screen">
        <div
          className={`flex-1 transition-all duration-300 transform ${
            isDebugPanelOpen ? "" : "translate-x-0"
          }`}
        >
          <ChatScrollView
            messages={messages}
            message={message}
            setMessage={setMessage}
            handleSubmit={handleSubmit}
            isGenerating={isGenerating}
            streamingMessage={streamingMessage}
            isGeneratingResponse={isGeneratingResponse}
            demo={demo}
          />
        </div>

        <Panels
          isUserAttributesOpen={isUserAttributesOpen}
          isDebugPanelOpen={isDebugPanelOpen}
          setIsDebugPanelOpen={setIsDebugPanelOpen}
          adText={adText}
          isGenerating={isGenerating}
          userMessages={userMessages}
        />
      </div>
    </>
  );
};
